import { Flex, Spinner, Text } from "@chakra-ui/react";
import { ProgressCircle } from "@iloq/partner-program/components/progress-circle";
import {
  getNPSAverageReportsCount,
  useNpsAverageQuery,
} from "@iloq/partner-program/query-hooks/use-nps-average-query";
import { ErrorMessage } from "@iloq/shared-react/error-message";
import { PropsWithChildren, ReactElement } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export function PartnerNps(): ReactElement {
  const npsAverageQuery = useNpsAverageQuery();
  const { formatMessage } = useIntl();

  function Wrapper({ children }: PropsWithChildren): ReactElement {
    return (
      <Flex flexDir="column" gap="2">
        <Flex height="40px" alignItems="center">
          <Text fontWeight="normal" textColor="brand.purpleDark">
            <FormattedMessage id="partnerProgram.hero.nps" />
          </Text>
        </Flex>
        <Flex flex="1">{children}</Flex>
      </Flex>
    );
  }

  if (npsAverageQuery.isFetching) {
    return (
      <Wrapper>
        <Spinner />
      </Wrapper>
    );
  }

  if (npsAverageQuery.isError) {
    return (
      <Wrapper>
        <ErrorMessage message={npsAverageQuery.error.message} />
      </Wrapper>
    );
  }

  const score = npsAverageQuery.data?.score ?? 0;

  const totalCounts = getNPSAverageReportsCount(npsAverageQuery.data);

  if (!score && !totalCounts) {
    return (
      <Wrapper>
        <Flex flexDir="row" gap="2" alignItems="center" height="40px">
          <Text
            fontWeight="normal"
            fontSize="4xl"
            lineHeight="40px"
            color="brand.grayDark"
            title={formatMessage({ id: "partnerProgram.npsAverage.noReports" })}
          >
            &ndash;
          </Text>
        </Flex>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Flex flexDir="row" gap="2" alignItems="center" height="40px">
        <ProgressCircle percentage={score} />
        <Text fontWeight="normal" fontSize="4xl" lineHeight="40px">
          {score}
        </Text>
      </Flex>
    </Wrapper>
  );
}
